// MissionReady Radio — LiveKit audio transport (USE_LIVEKIT sites only).
//
// Drop-in replacement for the legacy RadioSession mesh: same constructor,
// callbacks and connect/startTransmission/stopTransmission/destroy methods, so
// the Radio state machine, transcription and transcript saving are unchanged.
// LiveKit is the single owner of the Radio microphone: it acquires the mic on
// startTalking and releases it on stopTalking (stopMicTrackOnMute).
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type Participant,
  type RemoteTrack,
} from "livekit-client";
import type { RadioParticipant } from "./radio-state";
import type { RadioSessionCallbacks } from "./radio-webrtc";
import { onVoiceFocusChange, RADIO_DUCKED_VOLUME, isVoiceFocusBusy } from "./radio-mic-bus";

export type RadioConnectionState = "connecting" | "connected" | "reconnecting" | "disconnected";

export type LiveKitRadioCallbacks = RadioSessionCallbacks & {
  onConnectionState?: (s: RadioConnectionState) => void;
};

export class LiveKitRadioSession {
  private room: Room;
  private destroyed = false;
  private talking = false;
  private unsubFocus: (() => void) | null = null;
  private audioEls = new Set<HTMLMediaElement>();
  private speaking = new Set<string>();
  private resumeAudio = () => { void this.enableAudio(); };

  constructor(
    private readonly url: string,
    private readonly token: string,
    private readonly userId: string,
    private readonly cb: LiveKitRadioCallbacks,
  ) {
    this.room = new Room({
      adaptiveStream: false,
      dynacast: false,
      audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      publishDefaults: { stopMicTrackOnMute: true },
    });
    this.wire();
  }

  private wire() {
    const r = this.room;
    const emitParticipants = () => this.emitParticipants();
    r.on(RoomEvent.ParticipantConnected, emitParticipants);
    r.on(RoomEvent.ParticipantDisconnected, (p) => {
      if (this.speaking.delete(p.identity)) this.cb.onRemoteTransmission(p.identity, false);
      emitParticipants();
    });
    r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio) return;
      const el = track.attach();
      el.style.display = "none";
      el.volume = isVoiceFocusBusy() ? RADIO_DUCKED_VOLUME : 1;
      el.muted = this.talking;
      document.body.appendChild(el);
      this.audioEls.add(el);
    });
    r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((el) => {
        this.audioEls.delete(el);
        el.remove();
      });
    });
    r.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      this.cb.onPlaybackBlocked?.(!r.canPlaybackAudio);
    });
    r.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      const now = new Set(
        speakers.map((s) => s.identity).filter((id) => id !== this.userId),
      );
      for (const id of this.speaking) if (!now.has(id)) this.cb.onRemoteTransmission(id, false);
      for (const id of now) if (!this.speaking.has(id)) this.cb.onRemoteTransmission(id, true);
      this.speaking = now;
      emitParticipants();
    });
    r.on(RoomEvent.Reconnecting, () => this.cb.onConnectionState?.("reconnecting"));
    r.on(RoomEvent.Reconnected, () => {
      this.cb.onConnectionState?.("connected");
      emitParticipants();
    });
    r.on(RoomEvent.Disconnected, () => {
      this.cb.onConnectionState?.("disconnected");
      if (!this.destroyed) this.cb.onConnectionError("Radio connection lost — check your network");
    });

    // D.A.R. / Hey MR took the mic: stop talking and duck; restore after.
    this.unsubFocus = onVoiceFocusChange((busy) => {
      if (busy && this.talking) void this.stopTalking();
      this.audioEls.forEach((el) => (el.volume = busy ? RADIO_DUCKED_VOLUME : 1));
    });
  }

  private emitParticipants() {
    const list: RadioParticipant[] = [];
    const lp = this.room.localParticipant;
    if (lp?.identity) {
      list.push({
        userId: lp.identity,
        name: lp.name || "Officer",
        state: this.talking ? "transmitting" : "available",
      } as RadioParticipant);
    }
    this.room.remoteParticipants.forEach((p) => {
      list.push({
        userId: p.identity,
        name: p.name || "Officer",
        state: this.speaking.has(p.identity) ? "transmitting" : "available",
      } as RadioParticipant);
    });
    this.cb.onParticipants(list);
  }

  get connectionState(): RadioConnectionState {
    switch (this.room.state) {
      case ConnectionState.Connected:
        return "connected";
      case ConnectionState.Reconnecting:
      case ConnectionState.SignalReconnecting:
        return "reconnecting";
      case ConnectionState.Connecting:
        return "connecting";
      default:
        return "disconnected";
    }
  }

  /** Join the site room with the mic muted. LiveKit auto-reconnects. */
  async connect(): Promise<void> {
    document.addEventListener("pointerdown", this.resumeAudio);
    this.cb.onConnectionState?.("connecting");
    try {
      await this.room.connect(this.url, this.token, { autoSubscribe: true });
    } catch {
      throw new Error("Radio connection unavailable");
    }
    this.cb.onConnectionState?.("connected");
    void this.enableAudio();
    this.emitParticipants();
  }

  async disconnect(): Promise<void> {
    return this.destroy();
  }

  async enableAudio(): Promise<void> {
    try {
      await this.room.startAudio();
      this.cb.onPlaybackBlocked?.(!this.room.canPlaybackAudio);
    } catch { this.cb.onPlaybackBlocked?.(true); }
  }

  /** startTalking: LiveKit acquires and publishes the mic. */
  async startTalking(): Promise<MediaStream> {
    if (isVoiceFocusBusy()) throw new Error("mic-unavailable: in use by D.A.R. or Hey MR");
    this.audioEls.forEach((el) => { el.muted = true; });
    const pub = await this.room.localParticipant.setMicrophoneEnabled(true);
    const track = pub?.track?.mediaStreamTrack;
    if (!track) throw new Error("mic-unavailable");
    this.talking = true;
    this.emitParticipants();
    // Same mic track feeds Deepgram — no second getUserMedia.
    return new MediaStream([track]);
  }

  /** stopTalking: mute and release the mic. */
  async stopTalking(): Promise<void> {
    this.talking = false;
    try {
      await this.room.localParticipant.setMicrophoneEnabled(false);
    } catch {
      /* ignore */
    }
    this.audioEls.forEach((el) => { el.muted = false; });
    this.emitParticipants();
  }

  // RadioSession-compatible aliases used by useRadioLive.
  startTransmission() {
    return this.startTalking();
  }
  stopTransmission() {
    return this.stopTalking();
  }

  async destroy(): Promise<void> {
    if (this.destroyed) return;
    this.destroyed = true;
    document.removeEventListener("pointerdown", this.resumeAudio);
    this.unsubFocus?.();
    this.audioEls.forEach((el) => el.remove());
    this.audioEls.clear();
    try {
      await this.room.disconnect(true);
    } catch {
      /* ignore */
    }
  }
}
